import {defineComponent} from "vue";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {NormalContentBlockModel} from "@/models/api/pharma-cms-content-block/NormalContentBlockModel";
import {DpException} from "@/exception/DpException";
import {mapActions as mapActionsP, mapState} from "pinia";
import {CopyCpAbstractPayload} from "@/models/payload/CopyCpAbstractPayload";
import {useClipboardStore} from "@/stores/ClipboardStore";
import {useToast} from "vue-toastification";
import {DpEvent} from "@/models/DpEvent";
import {CbStructureApi} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureApi";
import {ContentBlockLinkRestService} from "@/services/rest/cms-content-block/ContentBlockLinkRestService";

/**
 * Shared behaviour of every content-block structure component of the WYSIWYG editor.
 *
 * Was AbstractCbCommon.vue. It lives in a .ts file so `tsc` type checks it — .vue files are skipped
 * by the project typecheck, which would leave the CbStructureApi assertion at the bottom unverified
 * until a full build.
 *
 * Subclasses override find/create/save/getDefaultCbModel/pasteCbMetadata, and optionally the
 * beforeReloadContent/afterReloadContent/beforeSubmitData/afterSubmitData hooks.
 */
export const CbStructureMixin = defineComponent({
    name: "CbStructureMixin",
    emits: ['exceptions', 'hasUnsavedChanges', 'abstractContentChanged'],
    props: {
        cbId: {
            type: String,
            required: false,
        },
        cbType: {
            type: String,
            required: true,
        },
        /**
         * The validation errors of the form this structure is rendered in, handed down by the host.
         *
         * A structure has no form of its own: its fields belong to the host's form, which is what
         * lets the host validate them before it saves. The errors therefore come from the host as
         * well, out of the slot of its GenericFormWrapper.
         */
        errors: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        isReady: false as boolean,
        error: null,
        contentBlock: new NormalContentBlockModel(),
        copyCpAbstractPayload: CopyCpAbstractPayload.createWithDefaults(),
        toaster: useToast(),
    }),
    watch: {
        cbId: {
            handler() {
                this.reloadContent();
            },
            immediate: true,
        },
    },
    computed: {
        ...mapState(useClipboardStore, ['getClipboardContentBlock']),
    },
    methods: {
        ...mapActionsP(useClipboardStore, ['copyToClipboardContentBlock']),
        async find(_payload: FindNormalContentBlockPayload): Promise<NormalContentBlockModel> {
            throw [new DpException('CbStructureMixin::find() should be overwritten by the dedicated component.')];
        },
        async create(_payload: CreateNormalContentBlockPayload): Promise<string> {
            throw [new DpException('CbStructureMixin::create() should be overwritten by the dedicated component.')];
        },
        async save(_payload: SaveNormalContentBlockPayload): Promise<void> {
            throw [new DpException('CbStructureMixin::save() should be overwritten by the dedicated component.')];
        },
        getDefaultCbModel(): NormalContentBlockModel {
            throw [new DpException('CbStructureMixin::getDefaultCbModel() should be overwritten by the dedicated component.')];
        },
        pasteCbMetadata(): void {
            throw [new DpException('CbStructureMixin::pasteCbMetadata() should be overwritten by the dedicated component.')];
        },
        buildPageLinkPayload(): void {
            throw [new DpException('CbStructureMixin::buildPageLinkPayload() should be overwritten by the dedicated component.')];
        },
        onCopyCpAbstractChanged(): void {
            this.$emit('abstractContentChanged', this.copyCpAbstractPayload);
        },
        onChange(): void {
            this.$emit('hasUnsavedChanges');
            this.onCopyCpAbstractChanged();
        },

        // --- CbStructureApi ---------------------------------------------------------------------

        async submit(): Promise<string | null> {
            await this.beforeSubmitData();

            let cbId: string | null = null;

            if (this.cbId === undefined) {
                cbId = await this.create(new CreateNormalContentBlockPayload(this.cbType, this.contentBlock));
            } else {
                await this.save(new SaveNormalContentBlockPayload(this.cbId, this.cbType, this.contentBlock));
            }

            await this.afterSubmitData();

            return cbId;
        },
        copyMetadataToClipboard(): void {
            this.copyToClipboardContentBlock(this.contentBlock, this.cbType);

            this.toaster.info(this.$t('cms.successfullyCopiedMetadata'));
        },
        pasteMetadataFromClipboard(): void {
            this.pasteCbMetadata();

            this.onChange();
            this.toaster.info(this.$t('cms.successfullyPastedMetadata'));
        },
        async applyLibraryContentBlock(libCbId: string): Promise<string | null> {
            let cbId: string | null = null;

            if (this.cbId === undefined) {
                cbId = await this.create(new CreateNormalContentBlockPayload(this.cbType, this.contentBlock));
            }

            await ContentBlockLinkRestService.getInstance()
                .createContentBlockLink(libCbId, this.cbType, this.cbId ?? cbId, false);

            // No reload here on purpose: this.cbId is still the stale prop at this point, so reloading
            // would replace the freshly linked block with an empty default. The caller persists the
            // column and reloads, which feeds the new cbId back in through the prop.
            return cbId;
        },

        // --- loading ----------------------------------------------------------------------------

        async reloadContent(): Promise<void> {
            this.isReady = false;
            const event = new DpEvent();
            try {
                await this.beforeReloadContent(event);

                if (event.isStopped()) {
                    this.isReady = true;
                    return;
                }

                this.onCopyCpAbstractChanged();

                if (this.cbId === undefined) {
                    this.contentBlock = this.getDefaultCbModel();
                } else {
                    this.contentBlock = await this.find(new FindNormalContentBlockPayload(this.cbId, this.cbType));
                }

                await this.afterReloadContent();
            } catch (exceptions: any) {
                // Loading is not driven by the parent's submit chain, so a failure here has nowhere to
                // propagate to; the host renders it.
                this.setExceptions(exceptions);
            }

            this.isReady = true;
        },
        async beforeReloadContent(_event: DpEvent): Promise<void> {
        },
        async afterReloadContent(): Promise<void> {
        },
        async beforeSubmitData(): Promise<void> {
        },
        async afterSubmitData(): Promise<void> {
        },
        setExceptions(exceptions: unknown): void {
            this.$emit('exceptions', exceptions);
        },
    }
});

/**
 * Compile-time proof that a component built on this mixin satisfies CbStructureApi. If a method is
 * renamed or its signature drifts, this fails to compile instead of failing at runtime in a ref call.
 */
function assertImplements<T>(_instance: T): void {
    // Type-level only; nothing to do at runtime.
}

assertImplements<CbStructureApi>({} as InstanceType<typeof CbStructureMixin>);

export default CbStructureMixin;
